import type { ReactNode } from "react";
import {
  Box,
  TextInput,
  SimpleGrid,
  Text,
  Flex,
  Tooltip,
} from "@mantine/core";
import "react-international-phone/style.css";
import { PhoneInput } from "react-international-phone";
import { IconLock } from "@tabler/icons-react";

interface PersonalDetailsProps {
  formData: {
    userName: string;
    userDesignation: string;
    userPhoneNumber: string;
    userPhoneCountryCode: string;
    companyEmail: string;
  };
  updateForm: (field: string, value: any) => void;
  isFieldLocked?: (fieldName: string) => boolean;
}

export default function PersonalDetails({
  formData,
  updateForm,
  isFieldLocked = () => false,
}: PersonalDetailsProps) {
  const lockedTooltip = "This field cannot be edited after verification";

  const lockLabel = (fieldKey: string, labelContent: ReactNode) => (
    <Flex align="center" gap={4}>
      {labelContent}
      {isFieldLocked(fieldKey) && (
        <Tooltip label={lockedTooltip} withArrow position="top">
          <Flex align="center">
            <IconLock size={12} color="#9CA3AF" />
          </Flex>
        </Tooltip>
      )}
    </Flex>
  );

  const phoneLocked = isFieldLocked("userPhoneNumber");
  const phoneInputBg = phoneLocked ? "var(--mantine-color-gray-1)" : "white";
  const phoneTextColor = phoneLocked ? "var(--mantine-color-gray-6)" : undefined;

  return (
    <Box>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="md">
        {/* Name */}
        <Box id="gt-field-userName">
          <TextInput
            label={lockLabel(
              "userName",
              <Text fz="sm" fw={500}>
                Name{" "}
                <Text span c="red" fz="lg">
                  *
                </Text>
              </Text>,
            )}
            placeholder="Enter your name"
            value={formData.userName}
            onChange={(e) => updateForm("userName", e.currentTarget.value)}
            disabled={isFieldLocked("userName")}
          />
        </Box>

        {/* Designation */}
        <Box id="gt-field-userDesignation">
          <TextInput
            label={lockLabel(
              "userDesignation",
              <Text fz="sm" fw={500}>
                Designation{" "}
                <Text span c="red" fz="lg">
                  *
                </Text>
              </Text>,
            )}
            placeholder="Enter your designation"
            value={formData.userDesignation}
            onChange={(e) => updateForm("userDesignation", e.currentTarget.value)}
            disabled={isFieldLocked("userDesignation")}
          />
        </Box>

        {/* Phone Number */}
        <Box id="gt-field-userPhoneNumber">
          <TextInput
            label={lockLabel(
              "userPhoneNumber",
              <Text fz="sm" fw={500}>
                Phone Number
              </Text>,
            )}
            placeholder="hidden"
            styles={{
              input: { display: "none" },
              wrapper: { display: "none" },
              root: { marginBottom: 0 },
            }}
            inputContainer={() => (
              <PhoneInput
                defaultCountry="in"
                value={`${formData.userPhoneCountryCode}${formData.userPhoneNumber}`}
                onChange={(phone, meta) => {
                  const countryCode = meta.country?.dialCode ? `+${meta.country.dialCode}` : "";
                  const nationalNumber = phone.replace(`+${meta.country?.dialCode || ""}`, "").trim();
                  updateForm("userPhoneCountryCode", countryCode);
                  updateForm("userPhoneNumber", nationalNumber);
                }}
                disabled={phoneLocked}
                hideDropdown={phoneLocked}
                placeholder="123-456-7890"
                style={{ width: "100%" }}
                inputStyle={{
                  width: "100%",
                  height: "36px",
                  fontSize: "14px",
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderLeft: "none",
                  borderRadius: "0 var(--mantine-radius-md) var(--mantine-radius-md) 0",
                  outline: "none",
                  backgroundColor: phoneInputBg,
                  color: phoneTextColor,
                }}
                countrySelectorStyleProps={{
                  buttonStyle: {
                    height: "36px",
                    border: "1px solid var(--mantine-color-gray-3)",
                    borderRadius: "var(--mantine-radius-md) 0 0 var(--mantine-radius-md)",
                    padding: "0 12px",
                    backgroundColor: phoneInputBg,
                    cursor: phoneLocked ? "default" : undefined,
                  },
                }}
              />
            )}
          />
        </Box>

        {/* Company Email (same field as company profile; shown here for context) */}
        <Box id="gt-field-companyEmail">
          <TextInput
            label={lockLabel(
              "companyEmail",
              <Text fz="sm" fw={500}>
                Company Email{" "}
                <Text span c="red" fz="lg">
                  *
                </Text>
              </Text>,
            )}
            placeholder="contact@company.com"
            value={formData.companyEmail}
            onChange={(e) => updateForm("companyEmail", e.currentTarget.value)}
            error={
              formData.companyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.companyEmail)
                ? "Please enter a valid email address"
                : null
            }
            disabled={isFieldLocked("companyEmail")}
          />
        </Box>
      </SimpleGrid>
    </Box>
  );
}
